import { useQuery } from '@tanstack/react-query';

import { rooms, type LiveRoom } from './api';

/** How often to ask who is on air. */
const POLL_MS = 45_000;

/**
 * Who is broadcasting right now.
 *
 * ## One query, three places
 *
 * The dashboard rail, the header's count and the modal behind it all want the
 * same list, and TanStack dedupes them onto a single request by key. That is
 * worth stating because the alternative is tempting and wrong: passing the list
 * down from the shell would couple a dashboard card to the layout, and fetching
 * it three times would poll a public endpoint three times a minute for one
 * answer.
 *
 * ## Polled, not pushed
 *
 * Going on air is rare and not urgent - a minute's delay in a rail costs
 * nothing. The socket is reserved for what happens *inside* a broadcast, where
 * a few seconds is the difference between hearing the same thing as everybody
 * else and not.
 *
 * Public, so it runs signed out too. A live broadcast is the best advertisement
 * the site has and hiding it behind an account is a wall in front of the reason
 * to make one.
 */
export function useLiveHosts() {
  const query = useQuery({
    queryKey: ['live-hosts'],
    queryFn: () => rooms.onAir(),
    refetchInterval: POLL_MS,
    /*
     * Not in the background. Nobody needs the rail refreshed while the tab is
     * buried, and a site left open all day would otherwise spend the whole day
     * asking.
     */
    refetchIntervalInBackground: false,
    staleTime: 20_000,
  });

  const items: LiveRoom[] = query.data?.items ?? [];

  return { ...query, items, count: items.length };
}
